import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Upload, Trash2, Plus, UserCheck, AlertCircle, Check, BookOpen, BarChart2, Mic, MessageSquare, Settings as SettingsIcon, DollarSign, TrendingUp, ArrowLeft, Download, Clock, Award, Activity as ActivityIcon } from '../../components/icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  fetchNLPPracticeConfig,
  saveNLPPracticeConfig,
  saveNLPPracticeOpenAIKey,
  fetchAuthorizedCoaches,
  authorizeCoach,
  toggleCoachAccess,
  removeCoachAccess,
  uploadKnowledgeFile,
  listKnowledgeFiles,
  deleteKnowledgeFile,
  fetchPartnerSessions,
  fetchPartnerCoachProgress,
  fetchSessionMessages,
  type NLPPracticeMessage,
  type CoachProgressSummary,
} from '../../lib/nlp-practice';
import { MarkdownRenderer } from '../../components/ai/MarkdownRenderer';
import {
  DEFAULT_META_MODEL_INSTRUCTIONS,
  DEFAULT_PRESUPPOSITIONS_INSTRUCTIONS,
  DEFAULT_MILTON_MODEL_INSTRUCTIONS,
  DEFAULT_NLP_TRAINER_INSTRUCTIONS,
} from '../../components/ai/nlpPracticeConstants';
import type { NLPPracticeConfig, NLPCoachAccess, NLPPracticeFile, NLPPracticeSession } from '../../lib/nlp-practice';
import { fetchNlpPracticeUsage, type NlpPracticeUsageSummary } from '../../lib/partner-dashboard';

type Tab = 'overview' | 'config' | 'knowledge' | 'coaches' | 'activity' | 'progress';

export function NLPPracticeManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [config, setConfig] = useState<NLPPracticeConfig | null>(null);
  const [authorizedCoaches, setAuthorizedCoaches] = useState<NLPCoachAccess[]>([]);
  const [allCoaches, setAllCoaches] = useState<{ id: string; name: string; email: string }[]>([]);
  const [files, setFiles] = useState<NLPPracticeFile[]>([]);
  const [sessions, setSessions] = useState<NLPPracticeSession[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [kbError, setKbError] = useState('');
  const [retryingKb, setRetryingKb] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [usageSummary, setUsageSummary] = useState<NlpPracticeUsageSummary | null>(null);
  const [coachProgress, setCoachProgress] = useState<CoachProgressSummary[]>([]);
  const [selectedCoach, setSelectedCoach] = useState<CoachProgressSummary | null>(null);
  const [coachSessions, setCoachSessions] = useState<NLPPracticeSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<NLPPracticeSession | null>(null);
  const [sessionMessages, setSessionMessages] = useState<NLPPracticeMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [progressFilter, setProgressFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  // Config form state
  const [isEnabled, setIsEnabled] = useState(false);
  const [metaModelInstructions, setMetaModelInstructions] = useState(DEFAULT_META_MODEL_INSTRUCTIONS);
  const [presuppositionsInstructions, setPresuppositionsInstructions] = useState(DEFAULT_PRESUPPOSITIONS_INSTRUCTIONS);
  const [miltonModelInstructions, setMiltonModelInstructions] = useState(DEFAULT_MILTON_MODEL_INSTRUCTIONS);
  const [nlpTrainerInstructions, setNlpTrainerInstructions] = useState(DEFAULT_NLP_TRAINER_INSTRUCTIONS);

  const loadData = useCallback(async () => {
    if (!user?.id) return;

    const { data: partner } = await supabase
      .from('partners')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!partner) {
      setLoading(false);
      return;
    }
    setPartnerId(partner.id);

    const [cfgResult, coachesResult, historyResult] = await Promise.allSettled([
      fetchNLPPracticeConfig(partner.id),
      fetchAuthorizedCoaches(partner.id),
      fetchPartnerSessions(partner.id),
    ]);

    const cfg = cfgResult.status === 'fulfilled' ? cfgResult.value : null;
    const coaches = coachesResult.status === 'fulfilled' ? coachesResult.value : [];
    const history = historyResult.status === 'fulfilled' ? historyResult.value : [];

    if (cfg) {
      setConfig(cfg);
      setIsEnabled(cfg.is_enabled);
      setMetaModelInstructions(cfg.system_instructions_meta_model || DEFAULT_META_MODEL_INSTRUCTIONS);
      setPresuppositionsInstructions(cfg.system_instructions_presuppositions || DEFAULT_PRESUPPOSITIONS_INSTRUCTIONS);
      setMiltonModelInstructions(cfg.system_instructions_milton_model || DEFAULT_MILTON_MODEL_INSTRUCTIONS);
      setNlpTrainerInstructions(cfg.system_instructions_nlp_trainer || DEFAULT_NLP_TRAINER_INSTRUCTIONS);
    }

    setAuthorizedCoaches(coaches);
    setSessions(history);

    try {
      const usage = await fetchNlpPracticeUsage(partner.id);
      setUsageSummary(usage);
    } catch {
      setUsageSummary(null);
    }

    try {
      const progress = await fetchPartnerCoachProgress(partner.id);
      setCoachProgress(progress);
    } catch {
      setCoachProgress([]);
    }

    // Fetch all coaches under this partner
    const { data: coachList } = await supabase
      .from('coaches')
      .select('id, user_id')
      .eq('partner_id', partner.id);

    if (coachList) {
      const enriched = await Promise.all(
        coachList.map(async (c) => {
          const { data: u } = await supabase
            .from('users')
            .select('full_name, email')
            .eq('id', c.user_id)
            .maybeSingle();
          return { id: c.id, name: u?.full_name || 'Unknown', email: u?.email || '' };
        }),
      );
      setAllCoaches(enriched);
    }

    try {
      const kbFiles = await listKnowledgeFiles();
      setFiles(kbFiles);
      setKbError('');
    } catch (err: any) {
      setFiles([]);
      setKbError(err.message || 'Failed to load knowledge base files');
    }

    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveConfig = useCallback(async () => {
    if (!partnerId) return;
    setSaving(true);
    setError('');
    try {
      await saveNLPPracticeConfig(partnerId, {
        is_enabled: isEnabled,
        system_instructions_meta_model: metaModelInstructions,
        system_instructions_presuppositions: presuppositionsInstructions,
        system_instructions_milton_model: miltonModelInstructions,
        system_instructions_nlp_trainer: nlpTrainerInstructions,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [partnerId, isEnabled, metaModelInstructions, presuppositionsInstructions, miltonModelInstructions, nlpTrainerInstructions]);

  const handleToggleEnabled = useCallback(async (newEnabled: boolean) => {
    if (!partnerId) return;
    setIsEnabled(newEnabled);
    try {
      await saveNLPPracticeConfig(partnerId, {
        is_enabled: newEnabled,
        ...(newEnabled && {
          system_instructions_meta_model: metaModelInstructions,
          system_instructions_presuppositions: presuppositionsInstructions,
          system_instructions_milton_model: miltonModelInstructions,
          system_instructions_nlp_trainer: nlpTrainerInstructions,
        }),
      });
      setConfig((prev) => prev ? { ...prev, is_enabled: newEnabled } : prev);
    } catch (err: any) {
      setError(err.message || 'Failed to update practice coach status');
      setIsEnabled(!newEnabled);
    }
  }, [partnerId]);

  const handleSaveApiKey = useCallback(async () => {
    if (!partnerId || !apiKeyInput.trim()) return;
    setSaving(true);
    setError('');
    try {
      await saveNLPPracticeOpenAIKey(partnerId, apiKeyInput.trim());
      setApiKeyInput('');
      setShowApiKey(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  }, [partnerId, apiKeyInput, loadData]);

  const handleUploadFile = useCallback(async (file: File) => {
    setUploading(true);
    setKbError('');
    try {
      await uploadKnowledgeFile(file);
      const kbFiles = await listKnowledgeFiles();
      setFiles(kbFiles);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err: any) {
      setKbError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    try {
      await deleteKnowledgeFile(fileId);
      setFiles(files.filter((f) => f.file_id !== fileId));
    } catch (err: any) {
      setKbError(err.message || 'Failed to delete file');
    }
  }, [files]);

  const handleRetryKb = useCallback(async () => {
    setRetryingKb(true);
    setKbError('');
    try {
      const kbFiles = await listKnowledgeFiles();
      setFiles(kbFiles);
    } catch (err: any) {
      setKbError(err.message || 'Failed to load knowledge base files');
    } finally {
      setRetryingKb(false);
    }
  }, []);

  const handleAuthorizeCoach = useCallback(async (coachId: string) => {
    if (!partnerId) return;
    try {
      await authorizeCoach(partnerId, coachId);
      const coaches = await fetchAuthorizedCoaches(partnerId);
      setAuthorizedCoaches(coaches);
    } catch (err: any) {
      setError(err.message || 'Failed to authorize coach');
    }
  }, [partnerId]);

  const handleToggleAccess = useCallback(async (accessId: string, isEnabled: boolean) => {
    try {
      await toggleCoachAccess(accessId, isEnabled);
      const coaches = partnerId ? await fetchAuthorizedCoaches(partnerId) : [];
      setAuthorizedCoaches(coaches);
    } catch (err: any) {
      setError(err.message || 'Failed to update access');
    }
  }, [partnerId]);

  const handleRemoveAccess = useCallback(async (accessId: string) => {
    try {
      await removeCoachAccess(accessId);
      const coaches = partnerId ? await fetchAuthorizedCoaches(partnerId) : [];
      setAuthorizedCoaches(coaches);
    } catch (err: any) {
      setError(err.message || 'Failed to remove access');
    }
  }, [partnerId]);

  const handleViewCoachDetail = useCallback((coach: CoachProgressSummary) => {
    setSelectedCoach(coach);
    const coachSessions = sessions.filter(s => s.coach_id === coach.coachId);
    setCoachSessions(coachSessions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
  }, [sessions]);

  const handleViewSessionDetail = useCallback(async (session: NLPPracticeSession) => {
    setSelectedSession(session);
    setSessionMessages([]);
    setLoadingMessages(true);
    try {
      const msgs = await fetchSessionMessages(session.id);
      setSessionMessages(msgs);
    } catch (err) {
      console.error('Error loading session messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const handleExportPDF = useCallback(async () => {
    if (!selectedCoach) return;
    setExporting(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `NLP-Practice-Report-${selectedCoach.coachName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      const opt = {
        margin: [10, 10, 10, 10] as number[],
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
      };
      await html2pdf().set(opt).from(pdfRef.current!).save();
    } catch (err) {
      console.error('PDF export failed:', err);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [selectedCoach]);

  const authorizedCoachIds = authorizedCoaches.map((c) => c.coach_id);
  const availableCoaches = allCoaches.filter((c) => !authorizedCoachIds.includes(c.id));

  if (loading) return <LoadingSpinner message="Loading NLP Practice settings..." />;

  const tabs: { id: Tab; label: string; icon: typeof SettingsIcon }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'config', label: 'Configuration', icon: SettingsIcon },
    { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
    { id: 'coaches', label: 'Coach Access', icon: UserCheck },
    { id: 'activity', label: 'Activity', icon: GraduationCap },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-brand-5 py-8 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-sage-600" />
            </div>
            <div>
              <h1 className="text-heading-sm text-gray-80">NLP Skills Practice Coach</h1>
              <p className="text-text-md text-gray-50">Manage your NLP training AI coach and authorize coaches to practice</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-destructive-5 text-destructive-40 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-1 mb-6 border-b border-cream-200 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id
                    ? 'border-sage-600 text-sage-700'
                    : 'border-transparent text-chocolate-400 hover:text-chocolate-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="freud-card-elevated p-5">
                <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Status</p>
                <p className={`text-lg font-bold ${config?.is_enabled ? 'text-sage-700' : 'text-chocolate-500'}`}>
                  {config?.is_enabled ? 'Active' : 'Inactive'}
                </p>
              </div>

              {!config?.is_enabled && authorizedCoaches.length > 0 && (
                <div className="col-span-2 md:col-span-4">
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">You have authorized coaches, but the practice coach is disabled. Coaches can't see NLP Practice yet.</p>
                    <button onClick={() => setTab('config')} className="ml-auto text-sm font-semibold text-amber-800 underline whitespace-nowrap">Enable now</button>
                  </div>
                </div>
              )}
              <div className="freud-card-elevated p-5">
                <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Authorized Coaches</p>
                <p className="text-lg font-bold text-chocolate-900">{authorizedCoaches.length}</p>
              </div>
              <div className="freud-card-elevated p-5">
                <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Total Sessions</p>
                <p className="text-lg font-bold text-chocolate-900">{sessions.length}</p>
              </div>
              <div className="freud-card-elevated p-5">
                <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">KB Files</p>
                <p className="text-lg font-bold text-chocolate-900">{files.length}</p>
              </div>
              <div className="freud-card-elevated p-5">
                <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Est. AI Cost (This Month)</p>
                <p className="text-lg font-bold text-rose-700">${(usageSummary?.totalCost ?? 0).toFixed(2)}</p>
                <p className="text-xs text-chocolate-400 mt-0.5">
                  ${(usageSummary?.totalVoiceCost ?? 0).toFixed(2)} voice + ${(usageSummary?.totalChatCost ?? 0).toFixed(2)} chat
                </p>
              </div>
            </div>

            <div className="freud-card-elevated p-6">
              <h3 className="text-base font-bold text-chocolate-900 mb-3">Getting Started</h3>
              <ol className="space-y-3 text-sm text-chocolate-600">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center text-xs font-bold text-sage-700">1</span>
                  <span>Add your OpenAI API key in the Configuration tab (or use the platform default).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center text-xs font-bold text-sage-700">2</span>
                  <span>Upload your NLP training material in the Knowledge Base tab.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center text-xs font-bold text-sage-700">3</span>
                  <span>Customize the system instructions for each module if needed.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center text-xs font-bold text-sage-700">4</span>
                  <span>Enable the practice coach and authorize your coaches in the Coach Access tab.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center text-xs font-bold text-sage-700">5</span>
                  <span>Your coaches will see "NLP Practice" in their sidebar and can start practicing.</span>
                </li>
              </ol>
            </div>
          </motion.div>
        )}

        {/* Config Tab */}
        {tab === 'config' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="freud-card-elevated p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-brand-10 rounded-xl">
                  <SettingsIcon className="w-5 h-5 text-brand-40" />
                </div>
                <div>
                  <h2 className="text-heading-xs text-gray-80">OpenAI API Key</h2>
                  <p className="text-text-sm text-gray-40">
                    {config?.has_openai_key
                      ? 'Your API key is configured and encrypted.'
                      : 'Optional — the platform default key will be used if not provided.'}
                  </p>
                </div>
              </div>
              {showApiKey ? (
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder="sk-..."
                    className="freud-input flex-1"
                  />
                  <button
                    onClick={handleSaveApiKey}
                    disabled={saving || !apiKeyInput.trim()}
                    className="freud-btn-primary px-4 py-2 rounded-xl font-semibold whitespace-nowrap"
                  >
                    Save Key
                  </button>
                  <button
                    onClick={() => { setShowApiKey(false); setApiKeyInput(''); }}
                    className="px-4 py-2 text-chocolate-500 hover:text-chocolate-700 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowApiKey(true)}
                  className="text-sm font-medium text-sage-700 hover:text-sage-800"
                >
                  {config?.has_openai_key ? 'Replace API Key' : 'Add API Key'}
                </button>
              )}
            </div>

            <div className="freud-card-elevated p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-heading-xs text-gray-80">Enable Practice Coach</h2>
                  <p className="text-text-sm text-gray-40">Turn on to allow authorized coaches to practice.</p>
                </div>
                <button
                  onClick={() => handleToggleEnabled(!isEnabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${isEnabled ? 'bg-sage-600' : 'bg-cream-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isEnabled ? 'translate-x-6' : ''}`} />
                </button>
              </div>
            </div>

            <div className="freud-card-elevated p-6">
              <h3 className="text-sm font-bold text-chocolate-900 mb-3">Meta Model Instructions</h3>
              <p className="text-xs text-chocolate-400 mb-3">System instructions for the Meta Model practice module. Customize as needed.</p>
              <textarea
                value={metaModelInstructions}
                onChange={(e) => setMetaModelInstructions(e.target.value)}
                rows={10}
                className="freud-input font-mono text-xs"
              />
            </div>

            <div className="freud-card-elevated p-6">
              <h3 className="text-sm font-bold text-chocolate-900 mb-3">Presuppositions Instructions</h3>
              <p className="text-xs text-chocolate-400 mb-3">System instructions for the Presuppositions practice module. Customize as needed.</p>
              <textarea
                value={presuppositionsInstructions}
                onChange={(e) => setPresuppositionsInstructions(e.target.value)}
                rows={10}
                className="freud-input font-mono text-xs"
              />
            </div>

            <div className="freud-card-elevated p-6">
              <h3 className="text-sm font-bold text-chocolate-900 mb-3">Milton Model Instructions</h3>
              <p className="text-xs text-chocolate-400 mb-3">System instructions for the Milton Model practice module. The 22 patterns are stored in the database and appended automatically.</p>
              <textarea
                value={miltonModelInstructions}
                onChange={(e) => setMiltonModelInstructions(e.target.value)}
                rows={10}
                className="freud-input font-mono text-xs"
              />
            </div>

            <div className="freud-card-elevated p-6">
              <h3 className="text-sm font-bold text-chocolate-900 mb-3">NLP Trainer AI Instructions</h3>
              <p className="text-xs text-chocolate-400 mb-3">System instructions for the NLP Trainer AI module. This module answers student questions about NLP, Timeline Therapy, Hypnosis, and NLP Coaching using your uploaded knowledge base. The source anonymization rule is built in — do not remove it.</p>
              <textarea
                value={nlpTrainerInstructions}
                onChange={(e) => setNlpTrainerInstructions(e.target.value)}
                rows={10}
                className="freud-input font-mono text-xs"
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="freud-btn-primary px-6 py-3 rounded-xl font-semibold whitespace-nowrap flex items-center gap-2"
              >
                {saved ? <Check className="w-4 h-4" /> : null}
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Configuration'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Knowledge Base Tab */}
        {tab === 'knowledge' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="freud-card-elevated p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-brand-10 rounded-xl">
                  <BookOpen className="w-5 h-5 text-brand-40" />
                </div>
                <div>
                  <h2 className="text-heading-xs text-gray-80">NLP Training Material</h2>
                  <p className="text-text-sm text-gray-40">Upload your approved NLP training documents. The AI coach will reference these during practice sessions.</p>
                </div>
              </div>

              <div className="border-2 border-dashed border-cream-300 rounded-xl p-8 text-center">
                <Upload className="w-10 h-10 text-cream-400 mx-auto mb-3" />
                <p className="text-sm text-chocolate-500 mb-2">Upload training material</p>
                <p className="text-xs text-chocolate-400 mb-4">PDF, TXT, MD, DOCX, CSV files supported</p>
                <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors cursor-pointer">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Choose File'}
                  <input
                    type="file"
                    accept=".pdf,.txt,.md,.docx,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadFile(file);
                      e.target.value = '';
                    }}
                    disabled={uploading}
                  />
                </label>
                {uploadSuccess && (
                  <p className="mt-3 text-sm text-sage-700 font-medium flex items-center justify-center gap-1">
                    <Check className="w-4 h-4" /> Upload successful!
                  </p>
                )}
              </div>
            </div>

            {kbError && (
              <div className="freud-card-elevated p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-warm-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{kbError}</p>
                </div>
                <button
                  onClick={handleRetryKb}
                  disabled={retryingKb}
                  className="px-4 py-2 bg-sage-600 text-white rounded-lg text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-50"
                >
                  {retryingKb ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}

            {files.length > 0 && (
              <div className="freud-card-elevated p-6">
                <h3 className="text-sm font-bold text-chocolate-900 mb-3">Uploaded Files</h3>
                <div className="space-y-2">
                  {files.map((file) => (
                    <div key={file.file_id} className="flex items-center justify-between p-3 bg-cream-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-sage-600" />
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">{file.filename}</p>
                          <p className="text-xs text-chocolate-400">
                            {(file.bytes / 1024).toFixed(1)} KB · {file.status}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteFile(file.file_id)}
                        className="p-2 text-warm-600 hover:bg-warm-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Coach Access Tab */}
        {tab === 'coaches' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="freud-card-elevated p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-brand-10 rounded-xl">
                  <UserCheck className="w-5 h-5 text-brand-40" />
                </div>
                <div>
                  <h2 className="text-heading-xs text-gray-80">Authorized Coaches</h2>
                  <p className="text-text-sm text-gray-40">Only coaches you authorize can access the NLP Practice Coach.</p>
                </div>
              </div>

              {!config?.is_enabled && authorizedCoaches.length > 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl mb-4">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">The practice coach is currently disabled. Coaches won't see NLP Practice until you enable it in the Configuration tab.</p>
                  <button onClick={() => setTab('config')} className="ml-auto text-sm font-semibold text-amber-800 underline whitespace-nowrap">Enable now</button>
                </div>
              )}

              {authorizedCoaches.length === 0 ? (
                <div className="text-center py-8">
                  <UserCheck className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                  <p className="text-sm text-chocolate-500">No coaches authorized yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {authorizedCoaches.map((coach) => (
                    <div key={coach.id} className="flex items-center justify-between p-3 bg-cream-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-sage-100 rounded-full flex items-center justify-center text-sm font-bold text-sage-700">
                          {coach.coach_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">{coach.coach_name}</p>
                          <p className="text-xs text-chocolate-400">{coach.coach_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleAccess(coach.id, !coach.is_enabled)}
                          className={`relative w-10 h-5 rounded-full transition-colors ${coach.is_enabled ? 'bg-sage-600' : 'bg-cream-300'}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${coach.is_enabled ? 'translate-x-5' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleRemoveAccess(coach.id)}
                          className="p-2 text-warm-600 hover:bg-warm-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {availableCoaches.length > 0 && (
              <div className="freud-card-elevated p-6">
                <h3 className="text-sm font-bold text-chocolate-900 mb-3">Add a Coach</h3>
                <div className="space-y-2">
                  {availableCoaches.map((coach) => (
                    <div key={coach.id} className="flex items-center justify-between p-3 bg-cream-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-cream-200 rounded-full flex items-center justify-center text-sm font-bold text-chocolate-500">
                          {coach.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">{coach.name}</p>
                          <p className="text-xs text-chocolate-400">{coach.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAuthorizeCoach(coach.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-600 text-white rounded-lg text-xs font-semibold hover:bg-sage-700 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Authorize
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Activity Tab */}
        {tab === 'activity' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="freud-card-elevated p-6">
              <h3 className="text-sm font-bold text-chocolate-900 mb-4">Practice Activity</h3>
              {sessions.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                  <p className="text-sm text-chocolate-500">No practice sessions yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-3 bg-cream-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-sage-100 rounded-xl flex items-center justify-center">
                          <span className="text-xs font-bold text-sage-700">
                            {s.module === 'meta_model' ? 'MM' : s.module === 'presuppositions' ? 'PS' : s.module === 'milton_model' ? 'ML' : 'NT'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">
                            {s.module === 'meta_model' ? 'Meta Model' : s.module === 'presuppositions' ? 'Presuppositions' : s.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {s.practice_style}
                          </p>
                          <p className="text-xs text-chocolate-400">
                            {new Date(s.created_at).toLocaleString()} · {s.mode === 'voice' ? 'Voice' : 'Text'} · {Math.floor(s.duration_seconds / 60)}m
                          </p>
                        </div>
                      </div>
                      {s.score !== null && (
                        <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                          (s.module === 'milton_model' ? s.score >= 8 : s.score >= 80) ? 'bg-sage-100 text-sage-700' :
                          (s.module === 'milton_model' ? s.score >= 6 : s.score >= 60) ? 'bg-amber-100 text-amber-700' :
                          'bg-warm-100 text-warm-700'
                        }`}>
                          {s.score}{s.module === 'milton_model' ? '/10' : '/100'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
        {/* Progress Tab */}
        {tab === 'progress' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {selectedSession ? (
              /* Session Detail View */
              <div>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to {selectedCoach?.coachName}'s sessions
                </button>
                <div className="freud-card-elevated overflow-hidden">
                  <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                        <span className="text-sm font-bold text-sage-700">
                          {selectedSession.module === 'meta_model' ? 'MM' : selectedSession.module === 'presuppositions' ? 'PS' : selectedSession.module === 'milton_model' ? 'ML' : 'NT'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-chocolate-900">
                          {selectedSession.module === 'meta_model' ? 'Meta Model' : selectedSession.module === 'presuppositions' ? 'Presuppositions' : selectedSession.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {selectedSession.practice_style}
                        </h3>
                        <p className="text-xs text-chocolate-400">
                          {new Date(selectedSession.created_at).toLocaleString()} · {selectedSession.mode === 'voice' ? 'Voice' : 'Text'} · {Math.floor(selectedSession.duration_seconds / 60)}m {selectedSession.duration_seconds % 60}s
                          {selectedSession.status === 'in_progress' && ' · In progress'}
                        </p>
                      </div>
                    </div>
                    {selectedSession.score !== null && (
                      <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                        (selectedSession.module === 'milton_model' ? selectedSession.score >= 8 : selectedSession.score >= 80) ? 'bg-sage-100 text-sage-700' :
                        (selectedSession.module === 'milton_model' ? selectedSession.score >= 6 : selectedSession.score >= 60) ? 'bg-amber-100 text-amber-700' :
                        'bg-warm-100 text-warm-700'
                      }`}>
                        {selectedSession.score}{selectedSession.module === 'milton_model' ? '/10' : '/100'}
                      </div>
                    )}
                  </div>

                  {selectedSession.feedback_summary && (
                    <div className="px-6 py-3 bg-cream-50 border-b border-cream-200">
                      <p className="text-xs font-bold text-chocolate-700 mb-1 uppercase tracking-wide">Practice Report</p>
                      <MarkdownRenderer content={selectedSession.feedback_summary} />
                    </div>
                  )}

                  <div className="px-6 py-4 overflow-y-auto max-h-[55vh] space-y-4">
                    {loadingMessages ? (
                      <div className="py-12 text-center">
                        <LoadingSpinner message="Loading conversation..." />
                      </div>
                    ) : sessionMessages.length === 0 ? (
                      <div className="py-12 text-center">
                        <MessageSquare className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                        <p className="text-chocolate-500">No messages were saved in this session.</p>
                        {selectedSession.mode === 'voice' && (
                          <p className="text-sm text-chocolate-400 mt-1">Voice sessions do not save text transcripts.</p>
                        )}
                      </div>
                    ) : (
                      sessionMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                            msg.role === 'user' ? 'bg-sage-600 text-white' : 'bg-cream-50 border border-cream-200'
                          }`}>
                            {msg.role === 'assistant' ? (
                              <MarkdownRenderer content={msg.content} />
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : selectedCoach ? (
              /* Coach Detail View */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setSelectedCoach(null)}
                    className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 transition-colors text-sm font-medium"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to all coaches
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? 'Exporting...' : 'Export PDF Report'}
                  </button>
                </div>

                {/* PDF Content (also visible on screen) */}
                <div ref={pdfRef} className="space-y-4">
                  <div className="freud-card-elevated p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-sage-100 rounded-full flex items-center justify-center text-xl font-bold text-sage-700">
                        {selectedCoach.coachName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-chocolate-900">{selectedCoach.coachName}</h2>
                        <p className="text-sm text-chocolate-400">{selectedCoach.coachEmail}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-cream-50 rounded-xl p-3">
                        <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Total Sessions</p>
                        <p className="text-lg font-bold text-chocolate-900">{selectedCoach.totalSessions}</p>
                      </div>
                      <div className="bg-cream-50 rounded-xl p-3">
                        <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Completed</p>
                        <p className="text-lg font-bold text-chocolate-900">{selectedCoach.completedSessions}</p>
                      </div>
                      <div className="bg-cream-50 rounded-xl p-3">
                        <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Practice Time</p>
                        <p className="text-lg font-bold text-chocolate-900">{selectedCoach.totalPracticeMinutes}m</p>
                      </div>
                      <div className="bg-cream-50 rounded-xl p-3">
                        <p className="text-xs text-chocolate-400 uppercase tracking-wide mb-1">Avg Score</p>
                        <p className="text-lg font-bold text-chocolate-900">
                          {selectedCoach.averageScore !== null ? `${selectedCoach.averageScore}/100` : '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Module Breakdown */}
                  <div className="freud-card-elevated p-6">
                    <h3 className="text-sm font-bold text-chocolate-900 mb-4">Module Breakdown</h3>
                    <div className="space-y-3">
                      {selectedCoach.moduleBreakdown.map((m) => (
                        <div key={m.module} className="flex items-center justify-between p-3 bg-cream-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-sage-100 rounded-xl flex items-center justify-center">
                              <span className="text-xs font-bold text-sage-700">
                                {m.module === 'meta_model' ? 'MM' : m.module === 'presuppositions' ? 'PS' : 'ML'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-chocolate-800">
                                {m.module === 'meta_model' ? 'Meta Model' : m.module === 'presuppositions' ? 'Presuppositions' : 'Milton Model'}
                              </p>
                              <p className="text-xs text-chocolate-400">{m.sessionCount} sessions</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {m.averageScore !== null && (
                              <div className="text-right">
                                <p className="text-xs text-chocolate-400">Avg</p>
                                <p className={`text-sm font-bold ${
                                  (m.isMilton ? m.averageScore >= 8 : m.averageScore >= 80) ? 'text-sage-700' :
                                  (m.isMilton ? m.averageScore >= 6 : m.averageScore >= 60) ? 'text-amber-700' :
                                  'text-warm-700'
                                }`}>
                                  {m.averageScore}{m.isMilton ? '/10' : '/100'}
                                </p>
                              </div>
                            )}
                            {m.highestScore !== null && (
                              <div className="text-right">
                                <p className="text-xs text-chocolate-400">Best</p>
                                <p className="text-sm font-bold text-sage-700">
                                  {m.highestScore}{m.isMilton ? '/10' : '/100'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Session History */}
                  <div className="freud-card-elevated p-6">
                    <h3 className="text-sm font-bold text-chocolate-900 mb-4">Session History</h3>
                    {coachSessions.length === 0 ? (
                      <p className="text-sm text-chocolate-500 text-center py-4">No sessions yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {coachSessions.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => handleViewSessionDetail(s)}
                            className="flex items-center justify-between p-3 bg-cream-50 rounded-xl cursor-pointer hover:ring-2 hover:ring-sage-300 transition-all"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-sage-100 rounded-xl flex items-center justify-center">
                                <span className="text-xs font-bold text-sage-700">
                                  {s.module === 'meta_model' ? 'MM' : s.module === 'presuppositions' ? 'PS' : s.module === 'milton_model' ? 'ML' : 'NT'}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-chocolate-800">
                                  {s.module === 'meta_model' ? 'Meta Model' : s.module === 'presuppositions' ? 'Presuppositions' : s.module === 'milton_model' ? 'Milton Model' : 'NLP Trainer AI'} — {s.practice_style}
                                </p>
                                <p className="text-xs text-chocolate-400">
                                  {new Date(s.created_at).toLocaleString()} · {s.mode === 'voice' ? 'Voice' : 'Text'} · {Math.floor(s.duration_seconds / 60)}m
                                  {s.status === 'in_progress' && ' · In progress'}
                                </p>
                              </div>
                            </div>
                            {s.score !== null && (
                              <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                                (s.module === 'milton_model' ? s.score >= 8 : s.score >= 80) ? 'bg-sage-100 text-sage-700' :
                                (s.module === 'milton_model' ? s.score >= 6 : s.score >= 60) ? 'bg-amber-100 text-amber-700' :
                                'bg-warm-100 text-warm-700'
                              }`}>
                                {s.score}{s.module === 'milton_model' ? '/10' : '/100'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Coach Progress Overview */
              <>
                {/* Team Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="freud-card-elevated p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="w-4 h-4 text-sage-600" />
                      <p className="text-xs text-chocolate-400 uppercase tracking-wide">Active Coaches</p>
                    </div>
                    <p className="text-lg font-bold text-chocolate-900">
                      {coachProgress.filter(c => c.hasPracticed).length}
                      <span className="text-sm text-chocolate-400 font-normal"> / {coachProgress.length}</span>
                    </p>
                  </div>
                  <div className="freud-card-elevated p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart2 className="w-4 h-4 text-sage-600" />
                      <p className="text-xs text-chocolate-400 uppercase tracking-wide">Total Sessions</p>
                    </div>
                    <p className="text-lg font-bold text-chocolate-900">
                      {coachProgress.reduce((sum, c) => sum + c.totalSessions, 0)}
                    </p>
                  </div>
                  <div className="freud-card-elevated p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-sage-600" />
                      <p className="text-xs text-chocolate-400 uppercase tracking-wide">Practice Hours</p>
                    </div>
                    <p className="text-lg font-bold text-chocolate-900">
                      {Math.floor(coachProgress.reduce((sum, c) => sum + c.totalPracticeMinutes, 0) / 60)}h {coachProgress.reduce((sum, c) => sum + c.totalPracticeMinutes, 0) % 60}m
                    </p>
                  </div>
                  <div className="freud-card-elevated p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <Award className="w-4 h-4 text-sage-600" />
                      <p className="text-xs text-chocolate-400 uppercase tracking-wide">Team Avg Score</p>
                    </div>
                    <p className="text-lg font-bold text-chocolate-900">
                      {(() => {
                        const scored = coachProgress.filter(c => c.averageScore !== null);
                        return scored.length > 0 ? `${Math.round(scored.reduce((a, b) => a + (b.averageScore || 0), 0) / scored.length)}/100` : '—';
                      })()}
                    </p>
                  </div>
                </div>

                {/* Inactive Coaches Alert */}
                {coachProgress.filter(c => !c.hasPracticed || (c.lastSessionDate && (Date.now() - new Date(c.lastSessionDate).getTime()) > 14 * 24 * 60 * 60 * 1000)).length > 0 && (
                  <div className="freud-card-elevated p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-semibold text-amber-800">Coaches needing a nudge</p>
                    </div>
                    <p className="text-xs text-chocolate-500">
                      {coachProgress.filter(c => !c.hasPracticed).length} haven't started ·{' '}
                      {coachProgress.filter(c => c.hasPracticed && c.lastSessionDate && (Date.now() - new Date(c.lastSessionDate).getTime()) > 14 * 24 * 60 * 60 * 1000).length} inactive 14+ days
                    </p>
                  </div>
                )}

                {/* Filter Buttons */}
                <div className="flex gap-2">
                  {(['all', 'active', 'inactive'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setProgressFilter(f)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        progressFilter === f ? 'bg-sage-600 text-white' : 'bg-cream-50 text-chocolate-500 hover:bg-cream-100'
                      }`}
                    >
                      {f === 'all' ? 'All Coaches' : f === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  ))}
                </div>

                {/* Coach Cards */}
                {coachProgress.length === 0 ? (
                  <div className="freud-card-elevated p-12 text-center">
                    <TrendingUp className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                    <p className="text-sm text-chocolate-500">No coaches found. Authorize coaches in the Coach Access tab to start tracking their progress.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {coachProgress
                      .filter(c => {
                        if (progressFilter === 'active') return c.hasPracticed;
                        if (progressFilter === 'inactive') return !c.hasPracticed || (c.lastSessionDate && (Date.now() - new Date(c.lastSessionDate).getTime()) > 14 * 24 * 60 * 60 * 1000);
                        return true;
                      })
                      .map((coach) => (
                        <div
                          key={coach.coachId}
                          onClick={() => handleViewCoachDetail(coach)}
                          className="freud-card-elevated p-5 cursor-pointer hover:ring-2 hover:ring-sage-300 transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 bg-sage-100 rounded-full flex items-center justify-center text-lg font-bold text-sage-700">
                                {coach.coachName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-bold text-chocolate-900">{coach.coachName}</p>
                                <p className="text-xs text-chocolate-400">
                                  {coach.hasPracticed
                                    ? `Last practiced ${coach.lastSessionDate ? new Date(coach.lastSessionDate).toLocaleDateString() : '—'}`
                                    : 'Has not started practicing'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {coach.hasPracticed ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sage-50 text-sage-700 rounded-full text-xs font-semibold">
                                  <ActivityIcon className="w-3 h-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream-100 text-chocolate-400 rounded-full text-xs font-semibold">
                                  Not started
                                </span>
                              )}
                            </div>
                          </div>

                          {coach.hasPracticed && (
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                              <div className="text-center bg-cream-50 rounded-lg p-2">
                                <p className="text-xs text-chocolate-400">Sessions</p>
                                <p className="text-base font-bold text-chocolate-900">{coach.totalSessions}</p>
                              </div>
                              <div className="text-center bg-cream-50 rounded-lg p-2">
                                <p className="text-xs text-chocolate-400">Completed</p>
                                <p className="text-base font-bold text-chocolate-900">{coach.completedSessions}</p>
                              </div>
                              <div className="text-center bg-cream-50 rounded-lg p-2">
                                <p className="text-xs text-chocolate-400">Minutes</p>
                                <p className="text-base font-bold text-chocolate-900">{coach.totalPracticeMinutes}</p>
                              </div>
                              <div className="text-center bg-cream-50 rounded-lg p-2">
                                <p className="text-xs text-chocolate-400">Avg Score</p>
                                <p className={`text-base font-bold ${coach.averageScore !== null ? (coach.averageScore >= 80 ? 'text-sage-700' : coach.averageScore >= 60 ? 'text-amber-700' : 'text-warm-700') : 'text-chocolate-300'}`}>
                                  {coach.averageScore !== null ? `${coach.averageScore}` : '—'}
                                </p>
                              </div>
                              {coach.moduleBreakdown.filter(m => m.sessionCount > 0).map(m => (
                                <div key={m.module} className="text-center bg-cream-50 rounded-lg p-2">
                                  <p className="text-xs text-chocolate-400">
                                    {m.module === 'meta_model' ? 'MM' : m.module === 'presuppositions' ? 'PS' : 'ML'}
                                  </p>
                                  <p className={`text-base font-bold ${m.averageScore !== null ? (m.isMilton ? (m.averageScore >= 8 ? 'text-sage-700' : m.averageScore >= 6 ? 'text-amber-700' : 'text-warm-700') : (m.averageScore >= 80 ? 'text-sage-700' : m.averageScore >= 60 ? 'text-amber-700' : 'text-warm-700')) : 'text-chocolate-300'}`}>
                                    {m.averageScore !== null ? `${m.averageScore}${m.isMilton ? '/10' : '/100'}` : `${m.sessionCount}`}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
